import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  ShowsLookupEntity,
  CompanyLookupEntity,
  ExhibitorLookupEntity,
  StatusLookupEntity,
  LocationsLookupEntity,
  CountryLookupEntity,
  ModeOfTransportLookupEntity,
  CityLookupEntity,
  UserTypeLookupEntity,
  TimezoneLookupEntity,
  DsvroleLookupEntity,
  VenueLookupEntity,
  OrganizerLookupEntity,
  CurrecyLookupEntity,
  UserDashboardCountEntity,
  mstrshowprefixddlentity,
  UOMLookupEntity,
  showuserentity,
  showdsvuserlistentity,
  ShowServicesEntity,
  MdgSearchDataEntity,
  FunctionalProcessDDl,
  FunctionlProcessStatusdll,
  OnsiteUserListEntitydll,
  AddressTypeLookupEntity,
  MdgDetailsEntity,
  SearchMdgRequestEntity,
  ShowByCountryLookupEntity,
  CustomerByShowLookupEntity,
  creditnotereasonddl,
  AppConfigEntity,
  SegmentLookupEntityDdl,
  CommonLookupEntityDdl,
  UomWeightCode
} from '../features/EfairsJob/types/common.lookup.types';
import { ENV } from "@app/constants/env";
import { getToken } from '@app/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL + ENV.MASTERS_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`); // use standard casing
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    // optional: retry once if unauthorized (adjust getToken refresh logic if available)
    if (result.error && result.error.status === 401 && token) {
      // If you have a refresh flow, invoke it here then retry.
      // const refreshed = await getRefreshedToken();
      // if (refreshed) { headers.set('Authorization', `Bearer ${refreshed}`); return rawBaseQuery(args, api, extraOptions); }
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  error?: string
}

export interface AddressEntity {
  mstr_Address_Id: number;
  mstr_Address_Type_Name: string;
  addressContent: string;
  Mstr_Address_Type_Id?: number;
}
export const commonDDLApi = createApi({
  reducerPath: 'commonlookupApi',
  baseQuery: authBaseQuery,
  tagTypes: ['CommonLookups'],
  endpoints: (builder) => ({
    getShowsForDDL: builder.query<ShowsLookupEntity[], { show_id: number; user_id?: number }>({
      query: ({ show_id, user_id = 0 }) => `/CommonLookup/getallshows?userId=${user_id}&ShowId=${show_id}`,
      providesTags: ['CommonLookups'],
    }),
    getUserShowsForDDL: builder.query<ShowsLookupEntity[], { user_id?: number } | void>({
      query: (arg) => {
        const user_id = arg?.user_id || 0;
        return `/CommonLookup/getallshows?userId=${user_id}&ShowId=0`;
      },
      providesTags: ['CommonLookups'],
    }),
    getShowsByCodeSearch: builder.query<ShowsLookupEntity[], { showcode: string }>({
      query: ({ showcode }) => `/CommonLookup/GetAllShowForRegistrationDDList/${showcode}`,
      providesTags: ['CommonLookups'],
    }),
    getCompanyForDDL: builder.query<CompanyLookupEntity[], { show_id: number }>({
      query: ({ show_id }) => `/CommonLookup/getallcompanies?showId=${show_id}`,
      providesTags: ['CommonLookups'],
    }),
    getCompanyForwithoutShowDDL: builder.query<CompanyLookupEntity[], void>({
      query: () => `/CommonLookup/getallcompanieslookup`,
      providesTags: ['CommonLookups'],
    }),
    getCompanyByCountryForDDL: builder.query<CompanyLookupEntity[], { country_id: number }>({
      query: ({ country_id }) => `/CommonLookup/GetCompaniesByCountry?countryId=${country_id}`,
      providesTags: ['CommonLookups'],
    }),
    getCompanyByIdForDDL: builder.query<CompanyLookupEntity[], { company_id: number }>({
      query: ({ company_id }) => `/CommonLookup/GetCompanyById?companyId=${company_id}`,
      providesTags: ['CommonLookups'],
    }),
    getExhibitorForDDL: builder.query<ExhibitorLookupEntity[], { show_id: number, user_id: number }>({
      query: ({ show_id, user_id }) => `/CommonLookup/getallexhibitors?ShowID=${show_id}&UserID=${user_id}`,
      providesTags: ['CommonLookups'],
    }),
    getStatusForDDL: builder.query<StatusLookupEntity[], void>({
      query: () => '/CommonLookup/getallstatus',
      providesTags: ['CommonLookups'],
    }),
    getWorkOrderByCustomerId: builder.query<any[], { customerId: number }>({
      query: ({ customerId }) => `/CommonLookup/GetWorkOrderByCustomerId?customerId=${customerId}`,
      providesTags: ['CommonLookups'],
    }),
    getPaymentAccountsType: builder.query<any[], void>({
      query: () => '/CommonLookup/GetPaymentAccountsType',
      providesTags: ['CommonLookups'],
    }),
    getAllCurrency: builder.query<any[], void>({
      query: () => '/CommonLookup/getallcurrency',
      providesTags: ['CommonLookups'],
    }),
    getServiceStatusForDDL: builder.query<StatusLookupEntity[], { serviceTypeId: number }>({
      query: ({ serviceTypeId }) => `/CommonLookup/GetAllServiceStatusByServiceTypeID?id=${serviceTypeId}`,
      providesTags: ['CommonLookups'],
    }),
    getLocationsForDDL: builder.query<LocationsLookupEntity[], { company_id: number }>({
      query: ({ company_id }) => `/CommonLookup/getalllocations?companyId=${company_id}`,
      providesTags: ['CommonLookups'],
    }),
    getCountriesForDDL: builder.query<CountryLookupEntity[], void>({
      query: () => '/CommonLookup/getallcountries',
      providesTags: ['CommonLookups'],
    }),
    getModesOfTransportForDDL: builder.query<ModeOfTransportLookupEntity[], void>({
      query: () => '/CommonLookup/getallmots',
      providesTags: ['CommonLookups'],
    }),
    getCitiesForDDL: builder.query<CityLookupEntity[], { countryId: number }>({
      query: ({ countryId }) => ({
        url: `/City/GetCities?Sid=${countryId}`,
        method: 'GET',
      }),
      providesTags: ['CommonLookups'],
    }),
    getUserTypesForDDL: builder.query<UserTypeLookupEntity[], void>({
      query: () => '/CommonLookup/getallusertypes',
      providesTags: ['CommonLookups'],
    }),
    getLicenseTypesForDDL: builder.query<UserTypeLookupEntity[], void>({
      query: () => '/CommonLookup/getalllicensetypes',
      providesTags: ['CommonLookups'],
    }),
    getTimezonesForDDL: builder.query<TimezoneLookupEntity[], void>({
      query: () => '/CommonLookup/getalltimezones',
      providesTags: ['CommonLookups'],
    }),
    getDsvrolesForDDL: builder.query<DsvroleLookupEntity[], void>({
      query: () => '/CommonLookup/getalldsvroles',
      providesTags: ['CommonLookups'],
    }),
    getVenuesForDDL: builder.query<VenueLookupEntity[], void>({
      query: () => '/CommonLookup/getallvenues',
      providesTags: ['CommonLookups'],
    }),
    getOrganizersForDDL: builder.query<OrganizerLookupEntity[], void>({
      query: () => '/CommonLookup/getallorganizers',
      providesTags: ['CommonLookups'],
    }),
    getCurrecyForDDL: builder.query<CurrecyLookupEntity[], void>({
      query: () => '/CommonLookup/getallcurrency',
      providesTags: ['CommonLookups'],
    }),
    getUserDBCountFor: builder.query<UserDashboardCountEntity[], { user_id: number, show_id: number }>({
      query: ({ user_id, show_id }) => `/CommonLookup/getuserdashboardcountdata?userId=${user_id}&showId=${show_id}`,
      providesTags: ['CommonLookups'],
    }),
    getshowprefixddl: builder.query<mstrshowprefixddlentity[], void>({
      query: () => '/CommonLookup/getallshowprefixesddl',
      providesTags: ['CommonLookups'],
    }),
    getuomforddl: builder.query<UOMLookupEntity[], void>({
      query: () => '/CommonLookup/getallUom',
      providesTags: ['CommonLookups'],
    }),
    getshowusers: builder.query<showuserentity[], { countryid: string }>({
      query: ({ countryid }) => `/CommonLookup/getusersbyshowcountry?showcountry=${countryid}`,
      providesTags: ['CommonLookups'],
    }),
    getselectedshowusers: builder.query<showuserentity[], { showcode: string }>({
      query: ({ showcode }) => `/CommonLookup/getselectedusersbyshowcode?showcode=${showcode}`,
      providesTags: ['CommonLookups'],
    }),
    getdsvshowuserlist: builder.query<showdsvuserlistentity[], { showid: number }>({
      query: ({ showid }) => `/CommonLookup/Getdsvuserlistforshow?showid=${showid}`,
      providesTags: ['CommonLookups'],
    }),
    getnondsvshowuserlist: builder.query<showdsvuserlistentity[], { showid: number }>({
      query: ({ showid }) => `/CommonLookup/Getuserlistforshow?showid=${showid}`,
      providesTags: ['CommonLookups'],
    }),
    getshowservices: builder.query<ShowServicesEntity[], { showid: number }>({
      query: ({ showid }) => `/CommonLookup/GetShowServices?showid=${showid}`,
      providesTags: ['CommonLookups'],
    }),
    getServicetypes: builder.query<showuserentity[], { showid: string }>({
      query: ({ showid }) => `/CommonLookup/getServiceTypebyShowId?showId=${showid}`,
      providesTags: ['CommonLookups'],
    }),
    sendMDGSearchData: builder.mutation<MdgSearchDataEntity[], { internalID: number; invoicing_Email: string; standardOrganisationName_Firstlinename: string; partyTaxID: string }>({
      query: (body) => ({
        url: '/CommonLookup/searchmdg', // replace with your actual endpoint
        method: 'POST',
        body,
      }),
    }),
    MDGSearchDataWOS: builder.mutation<MdgDetailsEntity[], any>({
      query: (body) => ({
        url: '/CommonLookup/searchmdg',
        method: 'POST',
        body,
      }),
    }),
    getfunctionalprocessddl: builder.query<FunctionalProcessDDl[], void>({
      query: () => '/CommonLookup/GetFunctionalProcessddl',
      providesTags: ['CommonLookups'],
    }),
    getstatusforfunctionalprocess: builder.query<FunctionlProcessStatusdll[], { userid: number, itemid: number }>({
      query: ({ userid, itemid }) => `/CommonLookup/GetStatusForFunctionalProcess?userid=${userid}&itemid=${itemid}`,
      providesTags: ['CommonLookups'],
    }),
    getAssignedUserList: builder.query<OnsiteUserListEntitydll[], { showid: number }>({
      query: ({ showid }) => `/CommonLookup/getAssignedUserList?showId=${showid}`,
      providesTags: ['CommonLookups'],
    }),
    getaddresstypeddl: builder.query<AddressTypeLookupEntity[], void>({
      query: () => '/CommonLookup/GetAddressTypeddl',
      providesTags: ['CommonLookups'],
    }),
    GetMDGSearchData: builder.mutation<ApiResponse<MdgDetailsEntity>, SearchMdgRequestEntity>({
      query: (request) => ({
        url: '/CommonLookup/Get_MDGSearchData',
        method: 'POST',
        body: request,
      }),
    }),
    getChargeCodeByUserId: builder.query<any[], { userId: number }>({
      query: ({ userId }) => `/ChargeCode/GetChargeCodeByUserId/${userId}`,
      providesTags: ['CommonLookups'],
    }),
    getFinanceType: builder.query<any[], { itemid: number }>({
      query: ({ itemid }) => `/CommonLookup/FinanceType?itemid=${itemid}`,
      providesTags: ['CommonLookups'],
    }),
    getAllTax: builder.query<any[], void>({
      query: () => ({
        url: '/Tax/GetAllTax',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          userID: 0,
          showId: "string",
          pageNumber: 0,
          pageSize: 0,
          totalRecord: 0,
          filtered: "string",
          sortColumn: "string",
          sortOrder: "string"
        },
      }),
      providesTags: ['CommonLookups'],
    }),
    getTaxByUser: builder.query<any[], { userId: number }>({
      query: ({ userId }) => `/Tax/GetTaxByUser/${userId}`,
      providesTags: ['CommonLookups'],
    }),
    getLineDescription: builder.query<any[], { Id: number }>({
      query: ({ Id }) => `/ChargeCode/GetLineDescription?Id=${Id}`,
      providesTags: ['CommonLookups'],
    }),
    getCreditnoteReasonddl: builder.query<creditnotereasonddl[], { itemid: number }>({
      query: ({ itemid }) => `/CommonLookup/CreditnoteReasonddl?itemid=${itemid}`,
      providesTags: ['CommonLookups'],
    }),
    getShowByCountryDDL: builder.query<ShowByCountryLookupEntity[], { countryId: number }>({
      query: ({ countryId }) => `/CommonLookup/GetShowByCountry?countryId=${countryId}`,
      providesTags: ['CommonLookups'],
    }),
    getCustomerByShowDDL: builder.query<CustomerByShowLookupEntity[], { showId: number, userId: number }>({
      query: ({ showId, userId }) => `/CommonLookup/GetCustomerByShow?showId=${showId}&userId=${userId}`,
      providesTags: ['CommonLookups'],
    }),
    getAddress: builder.mutation<AddressEntity[], number>({
      query: (showId) => ({
        url: `/Address/GetAddress/${showId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*'
        },
        body: '',
      }),
      transformResponse: (response: any) => Array.isArray(response) ? response : [],
    }),
    getDropdownList: builder.query<any[], { itemId: number }>({
      query: ({ itemId }) => ({
        url: `/CommonLookup/GetDropdownList?itemId=${itemId}`,
        method: 'GET',
        headers: { 'accept': '*/*' }
      }),
      transformResponse: (response: any) => Array.isArray(response) ? response : [],
      providesTags: ['CommonLookups'],
    }),
    updateAddress: builder.mutation<any, {
      mstr_Address_Id: number;
      mstr_Address_Type_ID: number;
      mstr_Address_Type_Name: string;
      addressContent: string;
      showcode?: string;
      mstr_Show_Id: number;
      created_By: number;
      edit_By?: number;
    }>({
      query: (payload) => ({
        url: `/Address/UpdateAddress/${payload.mstr_Address_Type_ID}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
      }),
    }),
    GetTransportCodesByMode: builder.query<any[], { modeOfTransportId: number }>({
      query: ({ modeOfTransportId }) => `/CommonLookup/GetTransportCodesByMode?modeOfTransportId=${modeOfTransportId}`,
      providesTags: ['CommonLookups'],
    }),
    getServicetypeById: builder.query<any[], { ServiceTypeId: number, showId: number }>({
      query: ({ ServiceTypeId, showId }) => `/CommonLookup/GetServiceTypeDataById?ServiceTypeId=${ServiceTypeId}&showId=${showId}`,
      providesTags: ['CommonLookups'],
    }),
    getEmailAppConfigValue: builder.query<AppConfigEntity, void>({
      query: () => '/CommonLookup/GetEmailAppConfigValue',
      providesTags: ['CommonLookups'],
    }),
    getChargecodeListByCompanyId: builder.query<any[], { companyId: number }>({
      query: ({ companyId }) => `/CommonLookup/GetChargecodeListByCompanyId?CompanyId=${companyId}`,
      providesTags: ['CommonLookups'],
    }),
    getStateListByCountryId: builder.query<any[], { countryId: number, stateType: number }>({
      query: ({ countryId, stateType }) => `/CommonLookup/GetStateListByCountryId?CountryId=${countryId}&stateType=${stateType}`,
      providesTags: ['CommonLookups'],
    }),
    getTaxLocations: builder.query<any[], { typeId: number }>({
      query: ({ typeId }) => `/CommonLookup/GetTaxLocations?typeId=${typeId}`,
      providesTags: ['CommonLookups'],
    }),
    GetSegmentForDDL: builder.query<SegmentLookupEntityDdl[], void>({
      query: () => '/CommonLookup/GetSegment',
      providesTags: ['CommonLookups'],
    }),
GetWeightForDDL: builder.query<CommonLookupEntityDdl[], void>({
      query: () => '/CommonLookup/GetWeight',
      providesTags: ['CommonLookups'],
 }),

    GetPackageTypeForDDL: builder.query<CommonLookupEntityDdl[], void>({
      query: () => '/CommonLookup/GetPackageType',
      providesTags: ['CommonLookups'],
    }),

  

    GetUomWeightCode: builder.query<UomWeightCode, { showId: number }>({
      query: ({ showId }) => `/CommonLookup/GetUomWeightCode?showId=${showId}`,
      providesTags: ['CommonLookups'],
    }),
GetSAData: builder.query<string, void>({
      query: () => '/CommonLookup/GetSAData',
      providesTags: ['CommonLookups'],
    }),
    GetLkcCustomerList: builder.query<string, { countryId: number }>({
      query: ({ countryId }) => `/CommonLookup/GetLkcCustomerList?countryId=${countryId}`,
      providesTags: ['CommonLookups'],
    }),
  

   
  }),

});

export const {
  useGetShowsForDDLQuery,
  useGetUserShowsForDDLQuery,
  useLazyGetShowsByCodeSearchQuery,
  useGetCompanyForDDLQuery,
  useGetCompanyForwithoutShowDDLQuery,
  useGetCompanyByCountryForDDLQuery,
  useGetCompanyByIdForDDLQuery,
  useLazyGetCompanyByIdForDDLQuery,
  useGetExhibitorForDDLQuery,
  useGetStatusForDDLQuery,
  useGetWorkOrderByCustomerIdQuery,
  useGetPaymentAccountsTypeQuery,
  useGetAllCurrencyQuery,
  useGetServiceStatusForDDLQuery,
  useGetLocationsForDDLQuery,
  useGetCountriesForDDLQuery,
  useGetModesOfTransportForDDLQuery,
  useGetCitiesForDDLQuery,
  useLazyGetCitiesForDDLQuery,
  useGetUserTypesForDDLQuery,
  useGetLicenseTypesForDDLQuery,
  useGetTimezonesForDDLQuery,
  useGetDsvrolesForDDLQuery,
  useGetVenuesForDDLQuery,
  useGetOrganizersForDDLQuery,
  useGetCurrecyForDDLQuery,
  useGetUserDBCountForQuery,
  useGetshowprefixddlQuery,
  useGetuomforddlQuery,
  useGetshowusersQuery,
  useGetselectedshowusersQuery,
  useGetdsvshowuserlistQuery,
  useGetnondsvshowuserlistQuery,
  useGetshowservicesQuery,
  useGetServicetypesQuery,
  useSendMDGSearchDataMutation,
  useMDGSearchDataWOSMutation,
  useGetfunctionalprocessddlQuery,
  useGetstatusforfunctionalprocessQuery,
  useGetAssignedUserListQuery,
  useGetaddresstypeddlQuery,
  useGetMDGSearchDataMutation,
  useGetChargeCodeByUserIdQuery,
  useGetFinanceTypeQuery,
  useGetAllTaxQuery,
  useGetTaxByUserQuery,
  useGetLineDescriptionQuery,
  useLazyGetLineDescriptionQuery,
  useGetShowByCountryDDLQuery,
  useGetCustomerByShowDDLQuery,
  useGetCreditnoteReasonddlQuery,
  useGetAddressMutation,
  useGetDropdownListQuery,
  useUpdateAddressMutation,
  useGetTransportCodesByModeQuery,
  useGetServicetypeByIdQuery,
  useGetEmailAppConfigValueQuery,
  useGetChargecodeListByCompanyIdQuery,
  useGetStateListByCountryIdQuery,
  useGetTaxLocationsQuery,

  useGetSegmentForDDLQuery,
  useGetWeightForDDLQuery,
  useGetPackageTypeForDDLQuery,
  useGetUomWeightCodeQuery,

  useGetSADataQuery,
  useGetLkcCustomerListQuery,

  

} = commonDDLApi;
 